import React, { useEffect, useMemo, useState } from "react";
import { Box, Grid, Card, CardContent, Typography } from "@mui/material";
import { useNavigate } from "react-router-dom";
import WorkIcon from "@mui/icons-material/Work";
import BusinessIcon from "@mui/icons-material/Business";
import PaymentsIcon from "@mui/icons-material/Payments";
import AssignmentIcon from "@mui/icons-material/Assignment";
import { BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer, LineChart, Line } from "recharts";
import { API_URL } from "../../../config";

const MONTHS = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];

function Admin() {
  const navigate = useNavigate();
  const [summary, setSummary] = useState(null);
  const [leadStatusData, setLeadStatusData] = useState([]);
  const [lineData, setLineData] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchAll = async () => {
      try {
        const [summaryRes, tasksRes, leadsRes] = await Promise.all([
          fetch(`${API_URL}/api/AdminSummary`),
          fetch(`${API_URL}/api/tasks/all`),
          fetch(`${API_URL}/api/getLeadData`),
        ]);

        const summaryJson = await summaryRes.json();
        const tasksJson = await tasksRes.json();
        const leadsJson = await leadsRes.json();

        setSummary(summaryJson);

        const leads = Array.isArray(leadsJson) ? leadsJson : leadsJson?.leads || [];
        const totalLeads = leads.length;
        const pending = leads.filter((l) =>
          ["pending", "followup"].includes(String(l?.status || "").toLowerCase().trim())
        ).length;
        const inProgress = leads.filter((l) =>
          ["schedule appointment", "proposal sent", "hold", "warm", "hot", "in progress"].includes(
            String(l?.status || "").toLowerCase().trim()
          )
        ).length;
        const closed = leads.filter((l) =>
          ["close", "closed", "other", "rejected", "win", "accepted", "converted"].includes(
            String(l?.status || "").toLowerCase().trim()
          )
        ).length;
        setLeadStatusData([
          { name: "In Progress Leads", value: inProgress },
          { name: "Total Leads", value: totalLeads },
          { name: "Closed Leads", value: closed },
        ]);

        const tasks = Array.isArray(tasksJson)
          ? tasksJson
          : tasksJson?.tasks || [];

        const monthCount = Array(12).fill(0);
        tasks.forEach((task) => {
          const rawDate = task?.updatedAt || task?.createdAt || task?.dueDate;
          if (!rawDate) return;
          const date = new Date(rawDate);
          if (Number.isNaN(date.getTime())) return;
          monthCount[date.getMonth()] += 1;
        });

        setLineData(
          MONTHS.map((month, idx) => ({
            month,
            value: monthCount[idx],
          }))
        );
      } catch (error) {
        console.error("Dashboard fetch error:", error);
      } finally {
        setLoading(false);
      }
    };

    fetchAll();
  }, []);

  const cards = useMemo(() => {
    if (!summary) return [];
    return [
      { title: "Total Projects", value: summary.totalProjects || 0, icon: <WorkIcon />, color: "#22c55e", route: "/admin/getProjectList" },
      { title: "Total Clients", value: summary.totalClients || 0, icon: <BusinessIcon />, color: "#06b6d4", route: "/admin/client" },
      { title: "Pending Payments", value: (summary.pendingPayments || 0).toLocaleString(), icon: <PaymentsIcon />, color: "#f59e0b", route: "/admin/InvoicesList" },
      { title: "Jobs Opening", value: summary.jobsOpening || 0, icon: <WorkIcon />, color: "#ef4444", route: "/admin/jobopening" },
      { title: "Total Tasks", value: summary.totalTasks || 0, icon: <AssignmentIcon />, color: "#64748b", route: "/admin/TaskList" },
      { title: "Pending Tasks", value: summary.pendingTasks || 0, icon: <AssignmentIcon />, color: "#fb923c", route: "/admin/TaskList?status=pending" },
      { title: "In Progress", value: summary.inProgressTasks || 0, icon: <AssignmentIcon />, color: "#10b981", route: "/admin/TaskList?status=in-progress" },
      { title: "Completed Tasks", value: summary.completedTasks || 0, icon: <AssignmentIcon />, color: "#a855f7", route: "/admin/TaskList?status=completed" },
    ];
  }, [summary]);

  if (loading) {
    return <div style={{ padding: "20px", textAlign: "center" }}>Loading...</div>;
  }

  if (!summary) {
    return <div style={{ padding: "20px", color: "red", textAlign: "center" }}>Failed to load dashboard data</div>;
  }

  return (
    <Box sx={{ p: 3, background: "#f3f4f6", minHeight: "100vh", color: "#0f172a" }}>
      <Typography variant="h4" sx={{ mb: 4, fontWeight: 600 }}>
        Dashboard Overview
      </Typography>

      <Grid container spacing={3}>
        {cards.map((card, index) => (
          <Grid item xs={12} sm={6} md={4} lg={3} key={index}>
            <Card
              onClick={() => card.route && navigate(card.route)}
              sx={{
                borderRadius: 3,
                background: "#ffffff",
                boxShadow: `0 0 20px ${card.color}40`,
                transition: "0.3s",
                cursor: card.route ? "pointer" : "default",
                "&:hover": {
                  transform: "translateY(-8px) scale(1.02)",
                  boxShadow: `0 0 30px ${card.color}`,
                },
              }}
            >
              <CardContent sx={{ display: "flex", alignItems: "center", gap: 2 }}>
                <Box
                  sx={{
                    background: card.color,
                    color: "#fff",
                    p: 1.5,
                    borderRadius: 2,
                    boxShadow: `0 0 15px ${card.color}`,
                  }}
                >
                  {card.icon}
                </Box>

                <Box>
                  <Typography variant="body2" sx={{ color: "#111827" }}>
                    {card.title}
                  </Typography>
                  <Typography variant="h5" sx={{ fontWeight: "bold", color: "#111827" }}>
                    {card.value}
                  </Typography>
                </Box>
              </CardContent>
            </Card>
          </Grid>
        ))}
      </Grid>

      <Box
        sx={{
          mt: 5,
          p: 3,
          borderRadius: 3,
          background: "#ffffff",
          boxShadow: "0 0 25px rgba(0,0,0,0.5)",
        }}
      >
        <Typography sx={{ mb: 2 }}>Leads Overview</Typography>
        <ResponsiveContainer width="100%" height={250}>
          <BarChart data={leadStatusData}>
            <XAxis dataKey="name" stroke="#111827" />
            <YAxis stroke="#111827" />
            <Tooltip />
            <Bar dataKey="value" radius={[10, 10, 0, 0]} fill="#003e6d" />
          </BarChart>
        </ResponsiveContainer>
      </Box>

      <Box
        sx={{
          mt: 4,
          p: 3,
          borderRadius: 3,
          background: "#ffffff",
        }}
      >
        <Typography sx={{ mb: 2 }}>Monthly Task Activity</Typography>
        <ResponsiveContainer width="100%" height={250}>
          <LineChart data={lineData}>
            <XAxis dataKey="month" stroke="#111827" />
            <YAxis stroke="#111827" />
            <Tooltip />
            <Line type="monotone" dataKey="value" stroke="#22c55e" strokeWidth={3} />
          </LineChart>
        </ResponsiveContainer>
      </Box>
    </Box>
  );
}

export default Admin;


